import { IRowPattern } from "../types/IPositionPatterns";
import { ISizeSummary } from "open-vessel-definition";
import { TCG_IN_MMM } from "../consts";
import { createRowsFromConfig } from "../createRowsFromConfig";
import { roundDec } from "@tedivo/tedivo-pure-helpers";

export interface IMissingTcgsInfo {
  /** TCGs of the vessel-wide scheme (as given by sizeSummary.centerLineRow) */
  allRowsTcgs: Record<IRowPattern, number>;
  /** TCGs to use in bay-levels with centerline row (00 = 0) */
  tcgsWithCenterLine: Record<IRowPattern, number>;
  /** TCGs to use in bay-levels without centerline row (01 = +half slot) */
  tcgsWithoutCenterLine: Record<IRowPattern, number>;
  calcMaxTcg: number | undefined;
  calcMinTcg: number | undefined;
}

/** Create the sets of TCGs (with and without centerline row) to use for missing values */
export function calculateMissingTcgs(
  missingImportantTcgs: boolean,
  sizeSummary: ISizeSummary,
): IMissingTcgsInfo {
  if (!missingImportantTcgs)
    return {
      allRowsTcgs: {},
      tcgsWithCenterLine: {},
      tcgsWithoutCenterLine: {},
      calcMaxTcg: undefined,
      calcMinTcg: undefined,
    };

  const withCl = buildTcgs(true, sizeSummary.maxRow);
  const withoutCl = buildTcgs(false, sizeSummary.maxRow);

  // Without the vessel-wide centerline row, no bay-level can have it
  const hasCenterLineRow = !!sizeSummary.centerLineRow;
  const used = hasCenterLineRow ? [withCl, withoutCl] : [withoutCl];

  return {
    allRowsTcgs: hasCenterLineRow ? withCl.tcgs : withoutCl.tcgs,
    tcgsWithCenterLine: withCl.tcgs,
    tcgsWithoutCenterLine: withoutCl.tcgs,
    calcMinTcg: Math.min(...used.map((u) => u.minTcg)),
    calcMaxTcg: Math.max(...used.map((u) => u.maxTcg)),
  };
}

function buildTcgs(centerLineRow: boolean, maxRowSummary: ISizeSummary["maxRow"]) {
  const rows = createRowsFromConfig(centerLineRow, maxRowSummary);

  let minTcg = Infinity;
  let maxTcg = -Infinity;

  // Odd rows go to starboard (+), even rows to port (-).
  // With centerline row, 00 is at 0; without it, rows 01 & 02 are half a slot away from 0.
  const offset = centerLineRow ? 0.5 : 0;

  const tcgs = rows.reduce((acc, t) => {
    const n = Number(t);
    const slots = n === 0 ? 0 : n % 2 === 1 ? n * 0.5 + offset : -(n * 0.5 - 0.5 + offset);
    acc[t] = roundDec(slots * TCG_IN_MMM, 2);
    if (minTcg > acc[t]) minTcg = acc[t];
    if (maxTcg < acc[t]) maxTcg = acc[t];
    return acc;
  }, {} as { [t: IRowPattern]: number });

  return { tcgs, minTcg, maxTcg };
}
